import React, { createContext, useContext, useState, useEffect } from 'react';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';

import {
    useGetProsjektProcessByIdQuery,
    useGetProsjektProcessByProcessIdQuery,
    useProsjektInvalidateTags,
} from '@klp/prosjekt/services/rtkApi';
import { ProsjektRoutes } from '@klp/prosjekt/config';
import type { GodkjenningProsessResponse } from '@klp/prosjekt/interfaces';

type SelectedProcessContextType = {
    prosjektId: string;
    initialProcessId: string;
    initialProcess?: GodkjenningProsessResponse;
    initialProcessIsLoading: boolean;
    selectedProcessId: string;
    setSelectedProcessId: (id: string) => void;
    selectedProcess?: GodkjenningProsessResponse;
    selectedProcessIsLoading: boolean;
    setSelectedProcess: (process?: GodkjenningProsessResponse) => void;
    currentProcess?: GodkjenningProsessResponse;
    currentProcessIsLoading: boolean;
    locale: string;
    invalidateProcess: () => void;
};

const SelectedProcessContext = createContext<
    SelectedProcessContextType | undefined
>(undefined);

export const useProsjektSammendragContext = (): SelectedProcessContextType => {
    const ctx = useContext(SelectedProcessContext);
    if (!ctx) {
        throw new Error(
            'useSelectedProcessContext must be used within SelectedProcessProvider',
        );
    }
    return ctx;
};

type Props = {
    prosjektId: string;
    initialProcessId: string;
    children: React.ReactNode;
};

export const ProsjektSammendragProvider: React.FC<Props> = ({
    prosjektId,
    initialProcessId,
    children,
}) => {
    const history = useHistory();
    const { i18n } = useTranslation();
    const [selectedProcessId, setSelectedProcessId] =
        useState(initialProcessId);
    const [selectedProcess, setSelectedProcess] =
        useState<GodkjenningProsessResponse>();

    const {
        data: currentProcess,
        isLoading: currentProcessIsLoading,
        isFetching: currentProcessIsFetching,
    } = useGetProsjektProcessByIdQuery({
        prosjektId,
        culture: i18n.language,
    });

    const {
        data: selectedProcessData,
        isLoading: selectedProcessIsLoading,
        isFetching: selectedProcessIsFetching,
    } = useGetProsjektProcessByProcessIdQuery(
        { prosessInstansId: selectedProcessId, culture: i18n.language },
        {
            skip: !selectedProcessId,
        },
    );

    const {
        data: initialProcess,
        isLoading: initialProcessIsLoading,
        isFetching: initialProcessIsFetching,
    } = useGetProsjektProcessByProcessIdQuery({
        prosessInstansId: initialProcessId,
        culture: i18n.language,
    });

    useEffect(() => {
        if (!currentProcess) return;
        if (
            selectedProcess &&
            selectedProcess.prosessInstansId !== currentProcess.prosessInstansId
        ) {
            return;
        }

        setSelectedProcess(currentProcess);
        history.replace(
            ProsjektRoutes.projectSummary(
                currentProcess.prosessInstansId,
                prosjektId,
            ),
        );
    }, [currentProcess, selectedProcess, prosjektId, history]);

    useEffect(() => {
        if (!selectedProcessData) return;
        if (selectedProcessData.prosessInstansId !== selectedProcessId) return;

        setSelectedProcess(selectedProcessData);
        history.replace(
            ProsjektRoutes.projectSummary(selectedProcessId, prosjektId),
        );
    }, [selectedProcessData, selectedProcessId, prosjektId, history]);

    const invalidateProcess = useProsjektInvalidateTags(['ProsjektProsess']);

    const contextValue: SelectedProcessContextType = {
        prosjektId,
        initialProcessId,
        initialProcess,
        initialProcessIsLoading:
            initialProcessIsLoading || initialProcessIsFetching,
        selectedProcessId,
        setSelectedProcessId,
        selectedProcess,
        selectedProcessIsLoading:
            selectedProcessIsLoading || selectedProcessIsFetching,
        setSelectedProcess,
        currentProcess,
        currentProcessIsLoading:
            currentProcessIsLoading || currentProcessIsFetching,
        locale: i18n.language,
        invalidateProcess,
    };

    return (
        <SelectedProcessContext.Provider value={contextValue}>
            {children}
        </SelectedProcessContext.Provider>
    );
};
